<script setup lang="ts">
import { computed, ref } from 'vue';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import ConfirmPopup from 'primevue/confirmpopup';
import InputText from 'primevue/inputtext';
import { useConfirm } from 'primevue/useconfirm';
import ProgressBar from 'primevue/progressbar';
import SplitButton from 'primevue/splitbutton';
import { MenuItem } from 'primevue/menuitem';
import {
  useDateFns, useStringFunctions, useAccountProfiles, useUtilities, useUserPreferences,
} from '@apparatix/composables';
import {
  Mask,
  Section,
  SplitBar,
  Stack,
  TitleBar,
  Toolbar,
  Typography,
} from '@apparatix/library/core';
import { button } from '@apparatix/theme/Ai';
import type { Section as SectionType, SectionEditCompleteData } from '@apparatix/types/AccountProfiles';
import ProfileBlobContent from './ProfileBlobContent.vue';
import ProfileListContent from './ProfileListContent.vue';
import ProfileTableContent from './ProfileTableContent.vue';
import ProfileCompositeListContent from './ProfileCompositeListContent.vue';
import ProfileBlobEditor from './ProfileBlobEditor.vue';
import ProfileListEditor from './ProfileListEditor.vue';
import ProfileTableEditor from './ProfileTableEditor.vue';
import ProfileCompositeListEditor from './ProfileCompositeListEditor.vue';
import ProfileSectionContentSwitch from './ProfileSectionContentSwitch.vue';
import ProfilePromptEditor from './ProfilePromptEditor.vue';
import AiSparklesIcon from '@apparatix/icons/AiSparklesIcon.vue';
import FeatureGate from '@apparatix/library/FeatureGate.vue';
import AiDisclaimer from './AiDisclaimer.vue';

type Props = {
  section: SectionType;
  isLoading: boolean;
  accountId: number;
  allowedAttempts: number;
  hasGeneratedProfile: boolean;
  acceptedDisclaimer: boolean;
  isSectionLoading: boolean;
}

type Emits = {
  (e: 'edit', data: SectionEditCompleteData): void;
  (e: 'on:loading', data: { isLoading: boolean, id: number }): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { formatDate } = useDateFns();
const { isEmptyString } = useStringFunctions();
const { deepClone, isObject, isEmptyObject } = useUtilities();
const {
  regenerateSection,
  validateSectionContents,
} = useAccountProfiles();
const confirm = useConfirm();
const {
  updatePreference,
} = useUserPreferences();

const isDisclaimerAccepted = ref(props.acceptedDisclaimer);

const calculateRemainingAttempts = (sectionAttempts: number) => {
  if (props.allowedAttempts - sectionAttempts <= 0) return 0;
  return props.allowedAttempts - sectionAttempts;
};

const isEditing = ref(false);
const isPrompting = ref(false);
const previousContent = ref<any>();
const newContent = ref<any>(props.section.unapprovedContents);
const prompt = ref('');
const promptResponseInvalid = ref(false);
const isRegenerating = ref(Boolean(props.section.unapprovedContents));
const canAcceptRegeneration = ref(true);
const remainingRegenerationAttempts = ref(calculateRemainingAttempts(props.section.executionCount));

const editedContent = ref<any>();

const updatedOn = computed(() => formatDate(props.section.updated_at, 'LL/dd/yy', ''));
const confirmClearGroup = computed(() => `account-profiles:${props.section.id}`);

const attemptsTried = computed(() => props.allowedAttempts - remainingRegenerationAttempts.value);

const hardFalseFutureToDo = ref(false);

const menuItems: MenuItem[] = [
  {
    label: 'Prompt',
    icon: 'pi pi-sparkles',
    command: () => beginPrompting(),
    disabled: !props.hasGeneratedProfile || remainingRegenerationAttempts.value <= 0,
  },
  {
    label: 'Edit',
    icon: 'pi pi-pen-to-square',
    command: () => beginEditing(),
  },
  // Intentionally disabled below for possible future work
  ...(hardFalseFutureToDo.value ? [
    {
      label: 'Clear',
      icon: 'pi pi-eraser',
      disabled: true,// !props.section.contents,
      command: () => confirm.require({
        group: confirmClearGroup.value,
        header: 'Clear Section',
        message: 'This will remove all content from this section',
        icon: 'pi pi-info-circle',
        rejectProps: {
          label: 'Cancel',
          size: 'small',
          severity: 'secondary',
        },
        acceptProps: {
          label: 'Confirm',
          size: 'small',
        },
        accept: () => emit('edit', { id: props.section.id, contents: null, source: 'user' }),
      }),
    },
  ]
    : []),
];

const splitButtonLabel = computed(() => {
  if (isEditing.value) return 'Editing';
  if (isRegenerating.value) return 'Regenerating';
  if (isPrompting.value) return 'Prompting';
  if (!props.hasGeneratedProfile) return 'Edit';
  return 'Regenerate';
});

const splitButtonIcon = computed(() => {
  const label = splitButtonLabel.value;
  if (label === 'Edit' || label === 'Editing') {
    return 'pi pi-pen-to-square';
  }

  return 'pi pi-sparkles';
});

const shouldShowAcceptControls = computed(() => isEditing.value || isPrompting.value || isRegenerating.value);

const onAcceptEdit = () => {
  let content: any;
  if (isPrompting.value || isRegenerating.value) {
    content = newContent.value;
  } else {
    content = editedContent.value;
  }

  if (!content) {
    return;
  }

  const updateSource = isEditing.value ? 'user' : 'ai';

  if (isObject(content) && isEmptyObject(content)) {
    return;
  }

  const update: SectionEditCompleteData = {
    id: props.section.id,
    contents: content,
    source: updateSource,
  };

  emit('edit', update);
  clearAllActions();
};

const onCancelEdit = () => {
  let content: any;

  // If we are canceling on a regenerated section, we need to update the backend so it resets the unapproved contents field.
  if (isPrompting.value || isRegenerating.value) {
    content = props.section.contents;

    const update: SectionEditCompleteData = {
      id: props.section.id,
      contents: content,
      source: props.section.userName === 'AI' ? 'ai' : 'user',
    };

    emit('edit', update);
  }

  clearAllActions();
};

const clearAllActions = () => {
  isEditing.value = false;
  isPrompting.value = false;
  isRegenerating.value = false;
  editedContent.value = undefined;
  previousContent.value = undefined;
  newContent.value = undefined;
  prompt.value = '';
};

const beginEditing = () => {
  if (isEditing.value) return;
  clearAllActions();
  isEditing.value = true;
};

const beginPrompting = () => {
  if (isPrompting.value) return;
  clearAllActions();
  isPrompting.value = true;
};

const onEditChange = (newValue: any) => {
  editedContent.value = newValue;
};

const determineSplitButtonAction = (e: MouseEvent) => {
  const label = splitButtonLabel.value;
  if (label === 'Editing' || label === 'Regenerating' || label === 'Prompting') {
    return;
  }

  if (label === 'Edit') {
    beginEditing();
  }

  if (label === 'Regenerate') {
    if (remainingRegenerationAttempts.value <= 0) {
      confirm.require({
        target: e.currentTarget as HTMLElement,
        group: 'account-profile-out-of-attempts',
        accept: () => { /* noop */ },
      });
    } else {
      newRegenerate();
    }
  }
};

const newRegenerate = async () => {
  if (! isDisclaimerAccepted.value) {
    confirm.require({
      group: 'ProfileDisclaimer',
      header: 'Disclaimer',
      rejectProps: {
        label: 'Decline',
        size: 'small',
        severity: 'secondary',
      },
      acceptProps: {
        label: 'Accept',
        size: 'small',
      },
      accept: async () => {
        updatePreference({ aiDisclaimerAccepted: 'yes' });
        isDisclaimerAccepted.value = true;
      },
    });
    return;
  }

  const promptValue = isEmptyString(prompt.value, true) ? undefined : prompt.value;
  emit('on:loading', { isLoading: true, id: props.section.id });

  if (!promptValue) {
    isRegenerating.value = true;
  }

  if (newContent.value) {
    previousContent.value = deepClone(newContent.value);
    newContent.value = undefined;
  }

  const newSection = await regenerateSection(props.accountId, props.section.id, promptValue);

  if (!newSection?.formatOption && props.section.formatOption) {
    newSection.formatOption = props.section.formatOption;
  }

  emit('on:loading', { isLoading: false, id: props.section.id });

  const isContentValid = validateSectionContents(newSection, true);
  if (isContentValid) {
    newContent.value = newSection.unapprovedContents;
  } else {
    promptResponseInvalid.value = true;
  }
};
</script>
<template>
  <Section variant="ai" :footer="Boolean(props.section?.contents) || shouldShowAcceptControls">
    <template #title>
      <TitleBar class="pb-3 md:pb-4 lg:pb-6">
        <template #title>
          <AiSparklesIcon />
          <Typography variant="section-heading">
            {{ props.section.title }}
          </Typography>
          <span v-tooltip="props.section.description" class="pi pi-question-circle ml-2" aria-label="Help icon" />
        </template>
        <template #button>
          <FeatureGate has="feature-ai-profiles">
            <SplitButton
              :label="splitButtonLabel"
              :icon="splitButtonIcon"
              aria-label="Generate section"
              class="p-splitbutton-ai"
              severity="secondary"
              :model="menuItems"
              :disabled="isRegenerating || isEditing || props.isSectionLoading"
              outlined
              @click="determineSplitButtonAction"
            />
            <template #fallback>
              <Button
                :label="isEditing ? 'Editing' : 'Edit'"
                icon="pi pi-pen-to-square"
                severity="secondary"
                :dt="button"
                @click="beginEditing"
              />
            </template>
          </FeatureGate>
        </template>
      </TitleBar>
    </template>
    <ProfileSectionContentSwitch :format="props.section.format">
      <template #blob>
        <ProfileBlobContent v-if="!isEditing && !isPrompting && !isRegenerating" :contents="props.section.contents" />
        <ProfileBlobEditor v-if="isEditing" :contents="props.section.contents" @on:change="onEditChange" />
      </template>
      <template #list>
        <ProfileListContent v-if="!isEditing && !isPrompting && !isRegenerating" :contents="props.section.contents" :columns="props.section.formatOption?.columns" />
        <ProfileListEditor
          v-if="isEditing"
          :contents="props.section.contents"
          :columns="props.section?.formatOption.columns"
          @on:change="onEditChange"
        />
      </template>
      <template #table>
        <ProfileTableContent
          v-if="!isEditing && !isPrompting && !isRegenerating"
          :contents="props.section.contents"
          :columns="props.section.formatOption?.columns"
          :is-contact="props.section.title === 'Decision Makers'"
        />
        <ProfileTableEditor
          v-if="isEditing"
          :contents="props.section.contents"
          :columns="props.section.formatOption?.columns"
          @on:change="onEditChange"
        />
      </template>
      <template #composite-list>
        <ProfileCompositeListContent v-if="!isEditing && !isPrompting && !isRegenerating" :contents="props.section.contents" />
        <ProfileCompositeListEditor v-if="isEditing" :contents="props.section.contents" @on:change="onEditChange" />
      </template>
      <template #fallback>
        <Typography variant="body" muted>
          Unable to process AI response, please try again
        </Typography>
      </template>
    </ProfileSectionContentSwitch>
    <ProfilePromptEditor
      v-if="isPrompting || isRegenerating"
      :new-content="newContent"
      :previous-content="previousContent ?? props.section.contents"
      :options="props.section.formatOption"
      :mark-invalid="promptResponseInvalid"
    />
    <template v-if="isPrompting">
      <Toolbar>
        <div class="grow">
          <InputText
            v-model="prompt"
            size="small"
            placeholder="What would you like to change?"
            fluid
          />
        </div>
        <div class="grow-0">
          <Button
            label="Send"
            icon="pi pi-send"
            size="small"
            severity="secondary"
            :disabled="props.section.isLoading"
            :dt="button"
            @click="newRegenerate"
          />
        </div>
      </Toolbar>
    </template>
    <template #footer>
      <SplitBar>
        <template #left>
          <Typography v-if="Boolean(props.section.userName) && Boolean(updatedOn)" variant="section-footer" muted>
            Updated by {{ props.section.userName }} on {{ updatedOn }}
          </Typography>
        </template>
        <template v-if="isEditing || isPrompting || isRegenerating" #right>
          <Button
            severity="secondary"
            label="Cancel"
            :disabled="props.section.isLoading"
            @click="onCancelEdit"
          />
          <!-- Try again button hard disabled to force user to select either keep or discard. If renabled there is a bug between new and previous prompt if trying again multiple times without leaving the page -->
          <Button
            v-if="isRegenerating"
            v-show="false"
            :label="`Try Again ${attemptsTried}/${props.allowedAttempts}`"
            severity="secondary"
            :dt="button"
            :disabled="remainingRegenerationAttempts <= 0 || props.section.isLoading"
            @click="newRegenerate"
          />
          <Button
            label="Accept"
            :disabled="props.section.isLoading || !canAcceptRegeneration"
            @click="onAcceptEdit"
          />
        </template>
      </SplitBar>
    </template>
    <Mask v-if="props.isLoading" class="rounded-lg">
      <Stack class="items-center w-full">
        <Typography variant="body">
          We're regenerating this section of your profile.
        </Typography>
        <Typography variant="body">
          This may take a little while. You can keep working, and we'll notify you once it's updated.
        </Typography>
        <ProgressBar mode="indeterminate" style="height: 5px; width: 75%;" />
      </Stack>
    </Mask>
  </Section>
  <ConfirmDialog :group="confirmClearGroup" />
  <ConfirmPopup group="account-profile-out-of-attempts">
    <template #container="{acceptCallback}">
      <Stack :gap="2" class="p-2 lg:p-4 rounded-sm">
        <Typography variant="body">
          You&rsquo;re out of generation attempts for this section
        </Typography>
        <Toolbar align="right">
          <Button
            label="Ok"
            size="small"
            @click="acceptCallback"
          />
        </Toolbar>
      </Stack>
    </template>
  </ConfirmPopup>
  <ConfirmDialog group="ProfileDisclaimerSection">
    <template #message>
      <AiDisclaimer />
    </template>
  </ConfirmDialog>
</template>
